import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { IReason } from '../../../reasons/data/reason';
import { ManagerReasonService } from 'src/app/pages/reasons/services/manager-reason.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { EReasonType } from '../../../reasons/data/reason-type.enum';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { EMessageType } from 'src/app/shared/data/response-default.interface';

@Component({
  selector: 'app-tab-reason',
  templateUrl: './tab-reason.component.html',
  styleUrls: ['./tab-reason.component.scss']
})
export class TabAddReasonComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  queueId = '';

  @Input() companyId?: string;
  @Input() partnerId?: string;

  formGroup = new UntypedFormGroup({
    reasonSelected: new UntypedFormControl([]),
    cancellationReasonsSelected: new UntypedFormControl([]),
    exceptionReasonSelected: new UntypedFormControl([])
  });

  reasonOptions: IReason[] = [];
  // Motivo de exceção só pode ser um dos motivos já vinculados à fila.
  exceptionReasonOptions: IReason[] = [];
  cancellationReasonsOptions: IReason[] = [];
  // O salvar apaga e recria os vínculos da fila: só libera depois de carregar o que ela já tem.
  motivosCarregados = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly managerReasonService: ManagerReasonService,
    private readonly toastService: ToastService,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.queueId = this.activatedRoute.snapshot.paramMap.get('id')!;

    this.formGroup
      .get('reasonSelected')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncExceptionReasons());

    if (this.companyId) {
      // As opções precisam chegar antes da seleção da fila para o sync de exceção ver as duas.
      forkJoin([
        this.managerReasonService.getCompanyReasons(this.companyId),
        this.managerReasonService.getQueueReasons(this.queueId)
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ([companyResponse, queueResponse]) => {
            this.reasonOptions = companyResponse.data.filter((reason) => reason.type === EReasonType.Atendimento);
            const queueReasons = queueResponse.data.filter((reason) => reason.type === EReasonType.Atendimento);
            this.formGroup
              .get('exceptionReasonSelected')
              ?.patchValue(queueReasons.filter((reason) => reason.isException).map((reason) => reason.id));
            this.formGroup.get('reasonSelected')?.patchValue(queueReasons.map((reason) => reason.id));
            this.motivosCarregados = true;
          },
          error: () =>
            this.toastService.toast(
              'Não foi possível carregar os motivos da fila. Recarregue a página antes de salvar.',
              EMessageType.Error
            )
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Mantém as opções e a seleção de exceção dentro dos motivos selecionados na fila.
  private syncExceptionReasons(): void {
    const selectedIds: string[] = this.formGroup.get('reasonSelected')?.value ?? [];
    this.exceptionReasonOptions = this.reasonOptions.filter((reason) => selectedIds.includes(reason.id));

    const exceptionControl = this.formGroup.get('exceptionReasonSelected');
    const exceptionIds: string[] = exceptionControl?.value ?? [];
    const keptIds = exceptionIds.filter((id) => selectedIds.includes(id));
    if (keptIds.length !== exceptionIds.length) {
      exceptionControl?.setValue(keptIds);
    }
  }

  addReasonToQueue(): void {
    if (!this.motivosCarregados) {
      return;
    }

    const reasons = [
      ...this.formGroup.get('reasonSelected')?.value,
      ...this.formGroup.get('cancellationReasonsSelected')?.value
    ];

    const body = {
      queueId: this.queueId!,
      reasonIds: reasons,
      exceptionReasonIds: this.formGroup.get('exceptionReasonSelected')?.value
    };
    this.managerReasonService.addReasonToQueue(body).subscribe((response) => {
      if (response.isSucceed) {
        this.toastService.toast('Motivos adicionados com sucesso');
      }
    });
  }
}
